import { ActionType } from '../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../core/api/module';
import * as R from 'ramda';
import { Endpoints } from '../../core/api/endpoints';
import { AppState } from '../../core/redux/appState';
import { ReportRecipientReport } from '../models';


const KEY_ACTION = ActionType.ReportRecipientReportsRequest;
const ACTIONS    = [
  ActionType.ReportRecipientReportsRequest,
  ActionType.ReportRecipientReportsResponse,
  ActionType.ReportRecipientReportsError,
];


type Payload = ReportRecipientReport[];


type Meta = {
  recipientId: number;
  projectIds?: number[];
  participantIds?: number[];
  dateRange?: number;
  lastOnly?: boolean;
  languageId?: number;
};


/**
 * Dispatch this to retrieve all the reports to which the recipient has access
 *
 * @param recipientId - the user's database person Id
 * @param projectIds - can be undefined or a list of project ids - defaults to empty list
 * @param participantIds - can be undefined or a list of participant ids - defaults to empty list
 * @param dateRange - can be undefined or the selected date range - defaults to 3 (last 3 months)
 * @param lastOnly: can be undefined or true to get only the last report - defaults to false
 * @param languageId: can be undefined or set to the current language - defaults to 1
 * @param userName: current user name
*/
export const reportRecipientReportsAction = (
  recipientId: number,
  userName: string,
  projectIds?: number[],
  participantIds?: number[],
  dateRange?: number,
  lastOnly?: boolean,
  languageId?: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.ReportRecipientReports,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      recipientId: recipientId,
      userName: userName,
      projectIds: projectIds,
      participantIds: participantIds,
      dateRange: dateRange,
      lastOnly: lastOnly,
      languageId: languageId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, {
                recipientId,
                projectIds,
                participantIds,
                dateRange,
                lastOnly,
                languageId,
              }),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${recipientId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.recipientId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const withOldReportsDeleted = R.dissoc('reportRecipientReports', successState);
  return R.assoc('reportRecipientReports', payload, withOldReportsDeleted) as AppState;
};

export const reportRecipientReportsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
